/**
 * The authoring cards' view of the FEMA form structure.
 *
 * These types mirror `services/coms/src/routes/Forms/docx/formSections.ts`, and
 * the data is generated from the same Word descriptors that drive the export,
 * so a card cannot show a different set of boxes than the document it prints
 * to. Regenerate with `yarn --cwd services/coms docx:sections`.
 */

import artifact from '../generated/femaFormSections.json';

export type FormSectionKind = 'text' | 'boolean' | 'table' | 'auto';

export type FormSectionColumn = {
  field: string;
  label: string;
};

export type FormSection = {
  number: string | null;
  label: string;
  printedLabel: string;
  source: string;
  kind: FormSectionKind;
  columns?: FormSectionColumn[];
  capacity?: number;
  checkboxSources?: string[];
  group?: number;
};

export type FormSectionGroup = {
  number: string | null;
  label: string;
  printedLabel: string;
};

export type FormSectionSchema = {
  baseId: string;
  title: string;
  sections: FormSection[];
  groups?: FormSectionGroup[];
};

const SCHEMAS = artifact.forms as unknown as Record<string, FormSectionSchema>;

const INCIDENT_INFO_SOURCES = artifact.incidentInfoSources as Record<
  string,
  Record<string, string>
>;

const BOOLEAN_INCIDENT_INFO_SOURCES =
  artifact.booleanIncidentInfoSources as Record<string, string[]>;

/**
 * Descriptor source to the `ics_incident_info` property that stores it.
 * Declared in coms because the version snapshotter needs the same translation,
 * so the card and the snapshot cannot disagree about which columns a form
 * owns.
 */
export const incidentInfoSources = (baseId: string): Record<string, string> =>
  INCIDENT_INFO_SOURCES[baseId] ?? {};

/** Sources the record stores as booleans rather than text. */
export const booleanIncidentInfoSources = (baseId: string): string[] =>
  BOOLEAN_INCIDENT_INFO_SOURCES[baseId] ?? [];

const VERSIONED_FORMS = new Set(artifact.versionedForms as string[]);

/** Whether the form carries version history and signatures. */
export const isVersionedForm = (baseId: string): boolean =>
  VERSIONED_FORMS.has(baseId);

/** Base ids that have a generated schema, e.g. 'ICS_202'. */
export const schemaBaseIds = (): string[] => Object.keys(SCHEMAS);

export const hasFormSectionSchema = (baseId: string): boolean =>
  baseId in SCHEMAS;

export const getFormSectionSchema = (
  baseId: string
): FormSectionSchema | undefined => SCHEMAS[baseId];

/** The sections a user types into, in printed order. */
export const authoredSections = (schema: FormSectionSchema): FormSection[] =>
  schema.sections.filter((section) => section.kind !== 'auto');

/** Renders '5. Hospitals', or just the label when the box is unnumbered. */
export const sectionHeading = (
  section: FormSection | FormSectionGroup
): string =>
  section.number ? `${section.number}. ${section.label}` : section.label;

/**
 * A Pratus field the printed form has no box for, shaped as a section so the
 * collapsed extras render through the same code path as the printed ones.
 */
export const pratusSection = (
  source: string,
  label: string,
  kind: FormSectionKind = 'text'
): FormSection => ({
  number: null,
  label,
  printedLabel: label,
  source,
  kind,
});
